import  React from "react";
import { useState, useContext, useEffect } from "react";
import { makeStyles, Theme, createStyles } from "@mui/styles";
import DefinitionDataProvider from "~common/form/icg-definition-data-provider";
import {HotKeyMappingContext} from "~common/components/app-view/index";
import {keyMap} from '~common/components/app-view/key-mapping';
import { each } from 'lodash'
import DefinitionSectionViewer from '~forms/components/definition-section-view-fc';
import OCRDataSupervisor from "~common/components/ocr-data-supervisor";
import { CurrentAttachmentContext, RegionOfInterestContext } from "~common/form/icg-definition-data-provider";

import Mousetrap from 'mousetrap'
const mousetrap = new Mousetrap();

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      container: {
        width: "100%",
      }
    })
  );

// Component to handle ROI message listening inside the DefinitionDataProvider
const ROIMessageHandler: React.FC = () => {
    // Get context setters from the DefinitionDataProvider
    const [currentAttachment, setCurrentAttachment, currentPage, setCurrentPage, rotation, setRotation, scaleMode, setScaleMode, roiPath, setRoiPath, icgOCRData, setICGOCRData, rawOCRData, setRawOCRData, suppressOCRData, setSuppressOCRData] = useContext(CurrentAttachmentContext);
    const { setSelectedRegion, setRegionsOfInterest, setMouseSelectionMode, setMouseModeUserSelectable } = useContext(RegionOfInterestContext);

    // Listen for ROI updates from the main window
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            // console.log("handleMessage", event);
            if (!event || !event.data) {
                return;
            }
            
            const action = (event.data.action || '').toLowerCase();
            if (action === "suppressocrdatafetch") {
                return;
            }

            if (action === "suppressocrdatafetch") {
                setSuppressOCRData(event.data.suppressOCRDataFetch);
            }

            if (action === "roiupdate") {
                try {
                    const { data } = event.data as { data: any };
            
                    const path = String(data.roiPath || "").startsWith('/') ? String(data.roiPath || "").substring(1) : String(data.roiPath || "");
                    if (!data.roiPath || data.roiPath == "" || !data.icgOCRData || !data.icgOCRData.paths[path] ){
                        return
                    }
                    // Sync core viewer context
                    if (data.roiPath !== undefined) setRoiPath(data.roiPath);
                    // if (data.currentAttachment !== undefined) setCurrentAttachment(data.currentAttachment);
                    if (data.currentPage !== undefined && currentPage != data.currentPage) setCurrentPage(data.currentPage);
                    if (data.rotation !== undefined && rotation != data.rotation) setRotation(data.rotation);
                    if (data.scaleMode !== undefined && data.scaleMode !== null) setScaleMode(data.scaleMode);
                    if (data.icgOCRData !== undefined) setICGOCRData(data.icgOCRData);
                    if (data.rawOCRData !== undefined) setRawOCRData(data.rawOCRData);

                    // Optional explicit ROI/selection updates from sender
                    if (data.selectedRegion !== undefined) setSelectedRegion(data.selectedRegion);
                    if (data.regionsOfInterest !== undefined) setRegionsOfInterest(data.regionsOfInterest);
                    if (data.mouseSelectionMode !== undefined) setMouseSelectionMode(data.mouseSelectionMode);
                    if (data.mouseModeUserSelectable !== undefined) setMouseModeUserSelectable(!!data.mouseModeUserSelectable);

                    // If we have a roiPath and icgOCRData with paths, compute ROI immediately
                    if (data.roiPath && (data.icgOCRData || icgOCRData)) {
                        try {
                            const effectiveOCR = data.icgOCRData || icgOCRData;
                            const pathKey = String(data.roiPath).startsWith('/') ? String(data.roiPath).substring(1) : String(data.roiPath);
                            const matchingPath = effectiveOCR?.paths?.[pathKey];
                            if (matchingPath) {
                                const bb = matchingPath?.Geometry?.BoundingBox;
                                const pageIndex = matchingPath?.PageIndex;
                                if (bb) {
                                    const pathRegion = {
                                        x: bb.Left,
                                        y: bb.Top,
                                        width: bb.Width,
                                        height: bb.Height,
                                        style: { stroke: 'rgba(247, 184, 108, 0.6)', fill: 'rgba(247, 184, 108, 0.3)', strokeWidth: 1 },
                                    } as any;
                                    setRegionsOfInterest([pathRegion]);
                                } else {
                                    setRegionsOfInterest([]);
                                }
                                if (pageIndex != null) {
                                    setCurrentPage(pageIndex + 1);
                                }
                            }
                        } catch (e) {
                            // Non-fatal: supervisor will also try to compute ROI from icgOCRData
                        }
                    }

                    // Ensure selection UI is enabled when OCR data is present
                    if ((data.rawOCRData || rawOCRData)) {
                        setMouseModeUserSelectable(true);
                    }

                } catch (error) {
                    console.error("Failed to process ROI update in external window:", error);
                }
            }
        };

        window.addEventListener("message", handleMessage);
        
        // Notify the main window that this external window is ready to receive ROI updates
        if (window.opener) {
            try {
                window.opener.postMessage({ action: "externalWindowReady" }, window.location.origin);
            } catch (error) {
                console.error("Failed to send ready message to main window:", error);
            }
        }
        
        return () => {
            window.removeEventListener("message", handleMessage);
        };
    }, [setRoiPath, setCurrentAttachment, setCurrentPage, setRotation, setScaleMode, setICGOCRData, setRawOCRData]);

    return null; // This component doesn't render anything
};

export const ExternalDefinitionSectionWindow: React.FC<any> = ({
	
}) => {

    const classes = useStyles();  

    const [currentKeyMap, setCurrentKeyMap] = useState(null);
    const [suppressOCRData, setSuppressOCRData] = useState(false);
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            // console.log("handleMessage", event);
            if (!event || !event.data) {
                return;
            }
            
            const action = (event.data.action || '').toLowerCase();
            if (action === "suppressocrdatafetch") {
                return;
            }
            if (action === "onreadystatechange") {
                setSuppressOCRData(event.data.suppressOCRDataFetch);
            }
        }
        window.addEventListener("message", handleMessage);
        mousetrap.reset();
        const unbinds: Array<string> = [];
        each(keyMap, (entry: any, combo: any) => {
            mousetrap.bind(combo, function() {
                setCurrentKeyMap(entry);
            });
            unbinds.push(combo);
        });
        return () => {
            if (unbinds.length > 0) {
                mousetrap.unbind(unbinds);
            }
        };
    }, []);

    return (
        <div className={classes.container}>
            <DefinitionDataProvider suppressOCRFetch={suppressOCRData}>
                <HotKeyMappingContext.Provider value={[currentKeyMap, setCurrentKeyMap]}>
                    <ROIMessageHandler />
                    <OCRDataSupervisor/>
                    <DefinitionSectionViewer/>
                </HotKeyMappingContext.Provider>
            </DefinitionDataProvider>
           
      </div>
    )
}